import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  CheckCircle, XCircle, Loader2, Calendar, MapPin, UserPlus, Ticket
} from 'lucide-react';
import './EventCheckIn.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EventSelfCheckIn = () => {
  const { eventSlug } = useParams();
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    subscribe_newsletter: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEventInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventSlug]);

  const fetchEventInfo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventSlug}/check-in/info`);
      if (res.ok) {
        const data = await res.json();
        setEventInfo(data);
      } else {
        setError('Event not found');
      }
    } catch (err) {
      setError('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      const res = await fetch(`${API_URL}/api/events/${eventSlug}/self-check-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setSuccess(data);
        toast.success(data.message || 'Successfully checked in!');
      } else {
        setError(data.detail || 'Registration failed');
        toast.error(data.detail || 'Registration failed');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      toast.error('Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="check-in-page loading-state">
        <Loader2 size={40} className="spinner" />
        <p>Loading event...</p>
      </div>
    );
  }

  if (error && !eventInfo) {
    return (
      <div className="check-in-page error-state">
        <XCircle size={48} />
        <h2>Event Not Found</h2>
        <p>This event doesn't exist or registration is not available.</p>
        <Link to="/events" className="btn btn-primary">View All Events</Link>
      </div>
    );
  }

  // Success State
  if (success) {
    return (
      <div className="check-in-page success-state" data-testid="self-checkin-success">
        <div className="success-card">
          <CheckCircle size={64} className="success-icon" />
          <h1>You're Checked In!</h1>
          <h2>{eventInfo?.title}</h2>
          
          <div className="success-details">
            <p className="welcome-name">Welcome, <strong>{success.name}</strong>!</p>
            
            {success.was_already_registered ? (
              <p className="status-message">You were already registered and have now been checked in.</p>
            ) : (
              <p className="status-message">You've been registered and checked in for this event.</p>
            )}
            
            <div className="event-details-box">
              <p><Calendar size={18} /> {eventInfo?.date}</p>
              <p><MapPin size={18} /> {eventInfo?.venue}</p>
            </div>
          </div>
          
          <div className="success-actions">
            <button 
              className="btn btn-secondary"
              onClick={() => {
                setSuccess(null);
                setFormData({ name: '', email: '', phone: '', subscribe_newsletter: true });
              }}
            >
              <UserPlus size={18} /> Check In Another Person
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Registration Form
  return (
    <div className="check-in-page self-checkin-page" data-testid="self-checkin-page">
      <div className="self-checkin-card">
        <div className="card-header">
          <Ticket size={32} className="header-icon" />
          <h1>Self Check-In</h1>
          <h2>{eventInfo?.title}</h2>
          <p className="event-details">
            <span><Calendar size={16} /> {eventInfo?.date}</span>
            <span><MapPin size={16} /> {eventInfo?.venue}</span>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="self-checkin-form">
          <p className="form-intro">
            Register and check yourself in for this event. Already registered? 
            Just enter your email and we'll check you in.
          </p>
          
          {error && (
            <div className="form-error">
              <XCircle size={18} />
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              id="name"
              type="text"
              placeholder="Enter your full name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              data-testid="self-checkin-name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
              data-testid="self-checkin-email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number (optional)</label>
            <input
              id="phone"
              type="tel"
              placeholder="Enter your phone number"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              data-testid="self-checkin-phone"
            />
          </div>

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.subscribe_newsletter}
                onChange={(e) => setFormData({...formData, subscribe_newsletter: e.target.checked})}
              />
              <span>Contact me about future events and AMR news</span>
            </label>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary btn-large"
            disabled={submitting}
            data-testid="self-checkin-submit"
          >
            {submitting ? (
              <>
                <Loader2 size={20} className="spinner" />
                Checking In...
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                Register & Check In
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EventSelfCheckIn;
